import type { ContractCostMap } from '@supply-mover-2-0/core';
import {
	getApplicableContractCost,
	getApplicableContractPrice,
	isPriceFromContract,
	resolveEffectiveMinimumPrice,
} from '@supply-mover-2-0/core';
import type {
	DeaRegisteredOutOfStateModel,
	NonQualifyingLicenseReason,
	OrderCheckFiringLevel,
	OrderCheckLevel,
} from '@supply-mover-2-0/shared';
import {
	checkControlledSubstancePercentage,
	checkCreditHold,
	checkCreditLimit,
	checkLicenseRequirements,
	checkMaximumDiscount,
	checkMinimumOrderPrice,
	checkMinimumPrices,
	checkOutOfStock,
	checkPeriodQuantityLimits,
	checkPerOrderLimits,
	checkPriceBelowCost,
	checkScheduleRestrictions,
	checkShortDate,
	isControlledSchedule,
	normalizeDeaSchedule,
	selectLicenseForOrderCheck,
	SHIPPING_ADDRESS_NOT_FOUND_MESSAGE,
} from '@supply-mover-2-0/shared';

interface ValidationMessage {
	type:
		| 'perOrderLimit'
		| 'dailyLimit'
		| 'monthlyLimit'
		| 'minimumPrice'
		| 'priceBelowCost'
		| 'outOfStock'
		| 'shortDate'
		| 'licenseExpired'
		| 'licenseInvalid'
		| 'creditHold'
		| 'creditLimit'
		| 'minimumOrderPrice'
		| 'maximumDiscount'
		| 'deaLicenseMissing'
		| 'itemTypeRestriction'
		| 'stateBoardLicenseInvalid'
		| 'deaLicenseInvalid'
		| 'shippingAddressNotFound'
		| 'controlledSubstancePercentage';
	level: 'warning' | 'holdWarning' | 'error';
	limit?: number;
	current?: number;
	todayQuantity?: number;
	monthQuantity?: number;
	message?: string;
	data?: unknown;
	isBlocking?: boolean;
}

interface ValidationState {
	hasError: boolean;
	hasWarning: boolean;
	isBlocking: boolean;
	messages: Array<ValidationMessage>;
	isOptimistic: boolean;
	timestamp: number;
}

interface OrderValidationState {
	hasError: boolean;
	hasWarning: boolean;
	messages: Array<ValidationMessage>;
	timestamp: number;
}

interface CartItem {
	quantity: number;
	price: number;
	isOptimistic?: boolean;
}

interface AddressLicense {
	id: number;
	type: string;
	licenseNumber: string | null;
	expirationDate: string | null;
	isExpired: boolean;
	isExpiringSoon: boolean;
	// Present when the shop endpoint sends them, so the cart evaluates the
	// same primary license the server does when an address holds several of
	// one kind (PD-14845).
	externalId?: string | null;
	isPrimary?: boolean | null;
	// PD-16416: a bounded veterinary license never backs an order; the shared
	// check passes over it, or reports it when it is the only one of its kind.
	isNonQualifying?: boolean | null;
	// PD-16612: why, so the cart words it as submission does (e.g. a State
	// Board license that is a state CSR is not a state license).
	nonQualifyingReason?: NonQualifyingLicenseReason | null;
	// PD-16823: a DEA registered in another state than the selected shipping
	// address; the shared check fails the DEA check at its level.
	registeredOutOfState?: DeaRegisteredOutOfStateModel | null;
}

/**
 * The cart's check configs never carry 'hold' (PermissionMappingService maps
 * it to 'off', since it is server-only), so a finding here can only come back
 * at a level the cart renders.
 */
function toMessageLevel(
	level: OrderCheckFiringLevel,
): ValidationMessage['level'] {
	return level === 'hold' ? 'holdWarning' : level;
}

interface AddressControls {
	isRxAllowed: boolean;
	isSchedule2Allowed: boolean;
	isSchedule2NAllowed: boolean;
	isSchedule3Allowed: boolean;
	isSchedule3NAllowed: boolean;
	isSchedule4Allowed: boolean;
	isSchedule5Allowed: boolean;
}

interface ControlledSubstanceCheck {
	enabled: boolean;
	// CRM-specific fields (used by frontend validation)
	crmMaximumControlPercent: number;
	crmUnitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';
	// Legacy fields for backward compatibility
	maximumControlPercent: number;
	unitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';
	crm: 'off' | 'warning' | 'holdWarning' | 'error';
}

interface ContractPriceBreak {
	minimumQuantity: number;
	price: number;
	contractId: number;
	contractName: string;
}

interface ValidationContext {
	itemLimits: Record<number, number>;
	dailyLimits: Record<number, number>;
	dailyQuantities: Record<number, number>;
	monthlyLimits?: Record<number, number>;
	// Quantities ordered over the rolling last-30-days window (monthly limit)
	monthQuantities?: Record<number, number>;
	// The item's OWN minimum price, keyed by item id. Leave an item out when
	// its minimum is null: an explicit 0 is a real value that switches the
	// default minimum off for that item (PD-16173).
	minimumPrices: Record<number, number>;
	// PD-16173 — whether the default-minimum-price setting applies to the
	// signed-in user (resolved by crm-api through their role-scoped preference
	// set). When true, an item with no own minimum is held to the price the
	// shop shows: the contract tier at the cart quantity, else its list price
	// from `listPrices`, else 0. That is the same value the server stamps onto
	// the order line, so the pre-add warning matches the submit-time hold.
	defaultMinimumPriceEnabled?: boolean;
	listPrices?: Record<number, number>;
	stockLevels: Record<number, number>;
	// Fee, deposit, rental and service items (item.isNonInventory). They are
	// never physical stock, so the out-of-stock check skips them (PD-15021).
	nonInventoryItems?: Record<number, boolean>;
	// Items flagged short date (item.isShortDate). Drives the short-date
	// order-submission check's row warning, like stockLevels drives outOfStock.
	shortDates?: Record<number, boolean>;
	// State-resolved schedule per item (one row, or null when unscheduled for
	// the selected shipping address). Resolution happens server-side.
	effectiveDeaSchedules: Record<
		number,
		{
			licenseLevel: string | null;
			state: string | null;
			schedule: string | null;
		} | null
	>;
	contractPrices: Record<number, number>;
	contractPriceBreaks?: Record<number, Array<ContractPriceBreak>>;
	// Contract-driven cost breaks per item (same map the cart threads into GP).
	// When an item is on a contract cost, the below-cost check is skipped: the
	// contract cost is the authoritative acquisition/sell cost the backend locks
	// in at submit, so a cart price above it is not "below cost" even if it is
	// below the item's own stored cost. Mirrors the contractPriceBreaks bypass in
	// the minimum-price / below-cost checks. (PD-14364)
	contractCosts?: ContractCostMap;
	canOrderOverDailyLimit: boolean;
	canOrderOverDailyItemLimit: boolean;
	/**
	 * PD-13950 — the tenant holds the pharmacy capability. Without it the
	 * State Board, DEA and controlled-substance checks do not run, with no
	 * fail-safe for a scheduled line (owner decision 2026-10-08). The server
	 * already serves those checks as `off` to such a tenant; the cart applies
	 * the same rule itself so a stale or partial config cannot show them.
	 */
	pharmacyChecksEnabled: boolean;
	checkConfigs: Array<{
		type: string;
		level: 'off' | 'warning' | 'holdWarning' | 'error';
	}>;
	selectedAddressId?: number | null;
	// Client-level validation data
	clientCreditLimit?: number | null;
	clientCreditBalance?: number;
	// Orders submitted but not yet at the ERP. They count against the credit
	// limit here exactly as they do when the server checks the submit.
	clientInFlightTotal?: number;
	clientHasCreditHold?: boolean;
	// License data for address
	addressLicenses?: Array<AddressLicense>;
	// Order-level configuration
	minimumOrderPrice?: number;
	maximumDiscount?: { value: number; type: 'percentage' | 'amount' };
	// Address control flags (MedMover-specific)
	addressControls?: AddressControls;
	// True when the cart's selected shipping address no longer resolves to a
	// live record (e.g. it was retired by the NetSuite address sync). When set,
	// the per-item address-control/license checks are skipped — their inputs
	// fail-closed to "nothing allowed / no licenses", which would otherwise
	// surface as a misleading RX/license block — and a single blocking
	// "shipping address not found" order message is shown instead.
	addressNotFound?: boolean;
	// Which license kinds exist on the client's OTHER addresses (from the
	// shop endpoint's licensesElsewhere). Distinguishes "shipping address
	// does not match the licensed address" from "no license on file".
	licensesElsewhere?: { stateBoard: boolean; dea: boolean };
	// Controlled substance percentage check config
	controlledSubstanceCheck?: ControlledSubstanceCheck;
	// Size counts for UOM quantity calculation in controlled substance checks
	itemSizeCounts?: Record<number, number>;
	// Cost data per item for price-below-cost validation
	itemCosts?: Record<
		number,
		{
			sellCost: number | null;
			acquisitionCost: number | null;
			averageCost: number | null;
		}
	>;
	// Price below cost check configuration
	priceBelowCostCheck?: {
		enabled: boolean;
		costField: string;
		crm: 'off' | 'warning' | 'hold' | 'holdWarning' | 'error';
	};
}

type ValidationListener = (
	itemId: number,
	validationState: ValidationState,
) => void;
type OrderValidationListener = (
	orderValidationState: OrderValidationState,
) => void;

/**
 * Centralized service for managing cart validation state across the application.
 * Handles both optimistic and confirmed cart states for immediate user feedback.
 */
class CartValidationManagerClass {
	private readonly listeners: Map<number, Set<ValidationListener>> = new Map();
	private readonly orderListeners: Set<OrderValidationListener> = new Set();
	private readonly validationStates: Map<number, ValidationState> = new Map();
	private orderValidationState: OrderValidationState | null = null;
	private cartItems: Record<number, CartItem> = {};
	private context: ValidationContext | null = null;
	private orderTotals: { subtotal: number; discount: number; total: number } = {
		subtotal: 0,
		discount: 0,
		total: 0,
	};

	/**
	 * Set the validation context (limits, permissions, etc.)
	 */
	setValidationContext(context: ValidationContext): void {
		this.context = context;
		this.revalidateAll();
	}

	/**
	 * The level a check is configured at for the CRM, or `fallback` when the
	 * cart has no config for it (the cart's long-standing defaults and legacy
	 * permission fallbacks).
	 */
	private _resolveLevel(
		type: string,
		fallback: OrderCheckLevel,
	): OrderCheckLevel {
		return (
			this.context?.checkConfigs.find((config) => config.type === type)
				?.level ?? fallback
		);
	}

	/**
	 * Get the effective contract price for an item at a given quantity.
	 * Delegates to shared utility in @supply-mover-2-0/core.
	 */
	getContractPriceForQuantity(itemId: number, quantity: number): number | null {
		return getApplicableContractPrice(
			this.context?.contractPriceBreaks,
			itemId,
			quantity,
		);
	}

	/**
	 * Get all price breaks for an item (for display purposes)
	 */
	getContractPriceBreaks(itemId: number): Array<ContractPriceBreak> | null {
		return this.context?.contractPriceBreaks?.[itemId] ?? null;
	}

	/**
	 * Check if contract prices have been loaded.
	 * Returns true if contractPriceBreaks is not null (even if empty {}).
	 * This distinguishes between "not loaded yet" (null) and "loaded but no contracts" ({}).
	 */
	public areContractPricesLoaded(): boolean {
		return this.context?.contractPriceBreaks != null;
	}

	/**
	 * Update order totals for order-level validation
	 */
	updateOrderTotals(subtotal: number, discount: number, total: number): void {
		this.orderTotals = { subtotal, discount, total };
		this.validateOrder();
	}

	/**
	 * Update cart item data and trigger validation
	 */
	updateCartItem(
		itemId: number,
		cartItem: CartItem | null,
		isOptimistic = false,
	): void {
		if (cartItem === null) {
			delete this.cartItems[itemId];
		} else {
			this.cartItems[itemId] = {
				...cartItem,
				isOptimistic,
			};
		}

		this.validateItem(itemId);
	}

	/**
	 * Update multiple cart items at once
	 */
	updateCartItems(items: Record<number, CartItem>, isOptimistic = false): void {
		// Clear validation for items no longer in cart
		const newItemIds = new Set(
			Object.keys(items).map((id) => parseInt(id, 10)),
		);
		const currentItemIds = Object.keys(this.cartItems).map((id) =>
			parseInt(id, 10),
		);

		currentItemIds.forEach((itemId) => {
			if (!newItemIds.has(itemId)) {
				delete this.cartItems[itemId];
				this.clearValidation(itemId);
			}
		});

		// Update/validate items in the new cart state
		Object.entries(items).forEach(([itemIdStr, cartItem]) => {
			const itemId = parseInt(itemIdStr, 10);
			this.cartItems[itemId] = {
				...cartItem,
				isOptimistic,
			};
			this.validateItem(itemId);
		});
	}

	/**
	 * Subscribe to validation state changes for a specific item
	 */
	subscribe(itemId: number, listener: ValidationListener): () => void {
		if (!this.listeners.has(itemId)) {
			this.listeners.set(itemId, new Set());
		}

		this.listeners.get(itemId)!.add(listener);

		// Immediately call listener with current state
		const currentState = this.validationStates.get(itemId);
		if (currentState) {
			listener(itemId, currentState);
		}

		// Return unsubscribe function
		return () => {
			const itemListeners = this.listeners.get(itemId);
			if (itemListeners) {
				itemListeners.delete(listener);
				if (itemListeners.size === 0) {
					this.listeners.delete(itemId);
				}
			}
		};
	}

	/**
	 * Get current validation state for an item
	 */
	getValidationState(itemId: number): ValidationState | null {
		return this.validationStates.get(itemId) || null;
	}

	/**
	 * Validate a specific item and notify listeners.
	 *
	 * PD-16276 — every decision below is a shared rule from
	 * @supply-mover-2-0/shared (order-validation), the same function the
	 * server runs at submit. This method only resolves the configured level
	 * from the cart's check configs and turns findings into the messages the
	 * cart UI renders.
	 */
	private validateItem(itemId: number): void {
		if (!this.context) {
			return;
		}

		const context = this.context;
		const cartItem = this.cartItems[itemId];
		const cartQuantity = cartItem?.quantity || 0;
		const cartPrice = parseFloat(String(cartItem?.price || 0)) || 0;

		// Skip validation if no cart quantity
		if (cartQuantity <= 0) {
			this.clearValidation(itemId);
			return;
		}

		const messages: ValidationMessage[] = [];
		let hasError = false;
		let hasWarning = false;
		let isBlocking = false;

		const record = (message: ValidationMessage): void => {
			messages.push(message);
			if (message.level === 'error') {
				hasError = true;
			} else {
				hasWarning = true;
			}
		};

		// Per-order limit. With no configured check, the legacy permission
		// decides: a user who may not order over the limit gets an error.
		for (const finding of checkPerOrderLimits(
			[{ itemId, quantity: cartQuantity, limit: context.itemLimits[itemId] }],
			this._resolveLevel(
				'perOrderLimit',
				context.canOrderOverDailyLimit ? 'off' : 'error',
			),
		)) {
			record({
				type: 'perOrderLimit',
				level: toMessageLevel(finding.level),
				limit: finding.limit,
				current: cartQuantity,
			});
		}

		// Daily and monthly limits count what the client already ordered in
		// the period. Quantities are whole units here, as they always were.
		const numericCartQuantity = parseInt(String(cartQuantity || 0), 10) || 0;
		const todayQuantity =
			parseInt(String(context.dailyQuantities[itemId] || 0), 10) || 0;

		for (const finding of checkPeriodQuantityLimits(
			[
				{
					itemId,
					quantity: numericCartQuantity,
					limit: context.dailyLimits[itemId],
					alreadyOrdered: todayQuantity,
				},
			],
			this._resolveLevel(
				'dailyItemLimit',
				context.canOrderOverDailyItemLimit ? 'off' : 'error',
			),
		)) {
			record({
				type: 'dailyLimit',
				level: toMessageLevel(finding.level),
				limit: finding.limit,
				current: cartQuantity,
				todayQuantity,
			});
		}

		// Monthly (rolling last 30 days, PD-14038) has no legacy permission
		// fallback: it only runs when the check is configured.
		const monthQuantity =
			parseInt(String(context.monthQuantities?.[itemId] ?? 0), 10) || 0;

		for (const finding of checkPeriodQuantityLimits(
			[
				{
					itemId,
					quantity: numericCartQuantity,
					limit: context.monthlyLimits?.[itemId],
					alreadyOrdered: monthQuantity,
				},
			],
			this._resolveLevel('monthlyItemLimit', 'off'),
		)) {
			record({
				type: 'monthlyLimit',
				level: toMessageLevel(finding.level),
				limit: finding.limit,
				current: cartQuantity,
				monthQuantity,
			});
		}

		// Minimum price. The effective minimum is the item's own, else the
		// default minimum when the setting applies (PD-16173); a line priced
		// at its contract price is exempt. With no configured check the cart
		// warns and the order is held, the tenant default.
		const priceMatchesContract = isPriceFromContract(
			context.contractPriceBreaks,
			itemId,
			cartPrice,
			cartQuantity,
		);

		for (const finding of checkMinimumPrices(
			[
				{
					itemId,
					quantity: cartQuantity,
					price: cartPrice,
					minimumPrice: resolveEffectiveMinimumPrice({
						itemMinimumPrice: context.minimumPrices[itemId],
						defaultMinimumPriceEnabled:
							context.defaultMinimumPriceEnabled === true,
						contractPrices: context.contractPriceBreaks,
						itemId,
						quantity: cartQuantity,
						listPrice: context.listPrices?.[itemId],
					}),
					priceMatchesContract,
				},
			],
			this._resolveLevel('minimumPrice', 'holdWarning'),
		)) {
			record({
				type: 'minimumPrice',
				level: toMessageLevel(finding.level),
				limit: finding.minimumPrice,
				current: cartPrice,
			});
		}

		// Price below cost, against the cost field the tenant chose. An item
		// on a contract cost is exempt as well as one on a contract price: the
		// contract cost is what the order locks in at submit (PD-14364).
		const priceBelowCostCheck = context.priceBelowCostCheck;

		if (priceBelowCostCheck != null) {
			const itemCost = context.itemCosts?.[itemId];
			const costField = priceBelowCostCheck.costField as keyof NonNullable<
				typeof itemCost
			>;

			for (const finding of checkPriceBelowCost(
				[
					{
						itemId,
						quantity: cartQuantity,
						price: cartPrice,
						cost: itemCost != null ? itemCost[costField] : null,
						priceMatchesContract,
						onContractCost:
							context.contractCosts != null &&
							getApplicableContractCost(
								context.contractCosts,
								itemId,
								cartQuantity,
							) != null,
					},
				],
				priceBelowCostCheck.enabled
					? this._resolveLevel('priceBelowCost', priceBelowCostCheck.crm)
					: 'off',
			)) {
				record({
					type: 'priceBelowCost',
					level: toMessageLevel(finding.level),
					limit: finding.cost,
					current: cartPrice,
					message: 'Price below cost',
				});
			}
		}

		// Out of stock, against the client-scoped stock the cart shows.
		for (const finding of checkOutOfStock(
			[
				{
					itemId,
					quantity: cartQuantity,
					quantityOnHand: context.stockLevels[itemId],
					isNonInventory: context.nonInventoryItems?.[itemId] === true,
				},
			],
			this._resolveLevel('outOfStock', 'off'),
		)) {
			record({
				type: 'outOfStock',
				level: toMessageLevel(finding.level),
				limit: finding.quantityOnHand,
				current: cartQuantity,
				message: 'Insufficient inventory',
			});
		}

		// Short date: the item carries the ERP short-date flag.
		for (const finding of checkShortDate(
			[
				{
					itemId,
					quantity: cartQuantity,
					isShortDate: context.shortDates?.[itemId] === true,
				},
			],
			this._resolveLevel('shortDate', 'off'),
		)) {
			record({
				type: 'shortDate',
				level: toMessageLevel(finding.level),
				message: 'Short date item',
			});
		}

		// DEA schedule restriction and licenses. effectiveDeaSchedules holds the
		// server-resolved schedule for this item at the selected shipping
		// address (state row > Federal > null).
		//
		// Skip them entirely when the cart's shipping address can't be
		// resolved: the address controls and licenses fail closed to "nothing
		// allowed / no licenses" for a missing address, which would wrongly
		// report "RX not allowed" / "license invalid" per item. The single
		// blocking "shipping address not found" message raised in validateOrder
		// covers this case instead.
		const effectiveSchedule = context.effectiveDeaSchedules[itemId];

		if (effectiveSchedule != null && context.addressNotFound !== true) {
			const deaType = normalizeDeaSchedule(effectiveSchedule.schedule);
			const scheduleLine = {
				itemId,
				quantity: cartQuantity,
				schedule: effectiveSchedule.schedule,
			};

			// The schedule restriction is always a blocking error, whatever the
			// check configs say.
			if (context.addressControls != null) {
				for (const finding of checkScheduleRestrictions(
					[scheduleLine],
					context.addressControls,
				)) {
					record({
						type: 'itemTypeRestriction',
						level: 'error',
						message: finding.message,
						data: { deaType, schedule: finding.schedule },
						isBlocking: true,
					});
					isBlocking = true;
				}
			}

			if (context.pharmacyChecksEnabled && context.addressLicenses != null) {
				const isControlled = isControlledSchedule(deaType);
				const stateBoardLicense = selectLicenseForOrderCheck(
					context.addressLicenses,
					'stateBoard',
				);
				const deaLicense = selectLicenseForOrderCheck(
					context.addressLicenses,
					'dea',
				);
				const findings = checkLicenseRequirements({
					lines: [scheduleLine],
					stateBoard: {
						level: this._resolveLevel('stateBoardLicense', 'error'),
						license: stateBoardLicense,
						existsElsewhere: context.licensesElsewhere?.stateBoard === true,
					},
					dea: {
						level: this._resolveLevel('deaLicense', 'error'),
						license: deaLicense,
						existsElsewhere: context.licensesElsewhere?.dea === true,
					},
				});

				// A controlled line reports its DEA license before its State
				// Board license, the order the cart has always listed them in.
				const ordered = isControlled
					? [
							...findings.filter((finding) => finding.kind === 'dea'),
							...findings.filter((finding) => finding.kind === 'stateBoard'),
						]
					: findings;

				for (const finding of ordered) {
					const level = toMessageLevel(finding.level);
					const blocks = level === 'error';
					let data: unknown;

					if (finding.kind === 'dea') {
						data = { deaType, deaLicense };
					} else if (isControlled) {
						data = { deaType, stateBoardLicense };
					} else {
						data = { deaType, license: stateBoardLicense };
					}

					record({
						type:
							finding.kind === 'dea'
								? 'deaLicenseInvalid'
								: 'stateBoardLicenseInvalid',
						level,
						message: finding.message,
						data,
						isBlocking: blocks,
					});

					if (blocks) {
						isBlocking = true;
					}
				}
			}
		}

		const validationState: ValidationState = {
			hasError,
			hasWarning,
			isBlocking,
			messages,
			isOptimistic: cartItem?.isOptimistic || false,
			timestamp: Date.now(),
		};

		this.validationStates.set(itemId, validationState);
		this.notifyListeners(itemId, validationState);
	}

	/**
	 * Clear validation for an item (when quantity becomes 0 or item removed)
	 */
	private clearValidation(itemId: number): void {
		const emptyState: ValidationState = {
			hasError: false,
			hasWarning: false,
			isBlocking: false,
			messages: [],
			isOptimistic: false,
			timestamp: Date.now(),
		};

		this.validationStates.set(itemId, emptyState);
		this.notifyListeners(itemId, emptyState);
	}

	/**
	 * Revalidate all items (when context changes)
	 */
	private revalidateAll(): void {
		Object.keys(this.cartItems).forEach((itemIdStr) => {
			const itemId = parseInt(itemIdStr, 10);
			this.validateItem(itemId);
		});
		this.validateOrder();
	}

	/**
	 * Validate order-level constraints (credit, minimum order, discount,
	 * controlled-substance share). As in validateItem, each decision is the
	 * shared rule the server runs at submit (PD-16276).
	 */
	private validateOrder(): void {
		if (this.context == null) {
			return;
		}

		const context = this.context;
		const messages: Array<ValidationMessage> = [];
		let hasError = false;
		let hasWarning = false;

		const record = (message: ValidationMessage): void => {
			messages.push(message);
			if (message.level === 'error') {
				hasError = true;
			} else {
				hasWarning = true;
			}
		};

		// Shipping address not found — the cart references an address that no
		// longer resolves to a live record (e.g. it was retired by the NetSuite
		// address sync). This blocks submission with a clear, address-specific
		// message; the per-item RX/license checks are suppressed for this case
		// (see validateItem) so the rep isn't shown misleading license errors.
		if (context.addressNotFound === true) {
			record({
				type: 'shippingAddressNotFound',
				level: 'error',
				message: SHIPPING_ADDRESS_NOT_FOUND_MESSAGE,
				isBlocking: true,
			});
		}

		const creditHold = checkCreditHold(
			context.clientHasCreditHold,
			this._resolveLevel('creditHold', 'error'),
		);

		if (creditHold != null) {
			record({
				type: 'creditHold',
				level: toMessageLevel(creditHold.level),
				message: 'Account is on credit hold',
			});
		}

		// Credit limit: the ERP balance, the orders already in flight and this
		// cart together may not pass the enforced limit.
		const creditLimit = checkCreditLimit(
			{
				creditLimit: context.clientCreditLimit,
				creditBalance: context.clientCreditBalance ?? 0,
				inFlightTotal: context.clientInFlightTotal ?? 0,
				orderTotal: this.orderTotals.total,
			},
			this._resolveLevel('creditLimit', 'error'),
		);

		if (creditLimit != null) {
			record({
				type: 'creditLimit',
				level: toMessageLevel(creditLimit.level),
				limit: creditLimit.creditLimit,
				current: creditLimit.projectedBalance,
				message: 'Order would exceed credit limit',
				data: {
					limit: creditLimit.creditLimit,
					balance: creditLimit.creditBalance,
					orderTotal: creditLimit.orderTotal,
					projected: creditLimit.projectedBalance,
				},
			});
		}

		const minimumOrder = checkMinimumOrderPrice(
			{
				minimum: context.minimumOrderPrice,
				subtotal: this.orderTotals.subtotal,
			},
			this._resolveLevel('minimumOrderPrice', 'error'),
		);

		if (minimumOrder != null) {
			record({
				type: 'minimumOrderPrice',
				level: toMessageLevel(minimumOrder.level),
				limit: minimumOrder.minimum,
				current: minimumOrder.subtotal,
				message: `Order total below minimum of $${minimumOrder.minimum}`,
				data: {
					minimum: minimumOrder.minimum,
					subtotal: minimumOrder.subtotal,
				},
			});
		}

		const maximumDiscount =
			context.maximumDiscount != null
				? checkMaximumDiscount(
						{
							maximum: context.maximumDiscount.value,
							type: context.maximumDiscount.type,
							discountAmount: this.orderTotals.discount,
							subtotal: this.orderTotals.subtotal,
						},
						this._resolveLevel('maximumDiscount', 'error'),
					)
				: null;

		if (maximumDiscount != null) {
			record({
				type: 'maximumDiscount',
				level: toMessageLevel(maximumDiscount.level),
				limit: maximumDiscount.maximum,
				current: maximumDiscount.actualDiscount,
				message: 'Discount exceeds maximum allowed',
				data: {
					discount: maximumDiscount.actualDiscount,
					maximum: maximumDiscount.maximum,
					type: maximumDiscount.type,
				},
			});
		}

		// Controlled substance share of the cart, by each line's schedule at
		// the shipping address. Uses the CRM-specific fields, falling back to
		// the legacy ones.
		// Partial: the wire shape can omit the crm-prefixed fields.
		// PD-13950 — a pharmacy check, so it needs the pharmacy capability.
		const controlCheck: Partial<ControlledSubstanceCheck> | undefined =
			context.pharmacyChecksEnabled
				? context.controlledSubstanceCheck
				: undefined;

		if (controlCheck != null) {
			const controlled = checkControlledSubstancePercentage(
				{
					lines: Object.entries(this.cartItems).map(([itemIdStr, cartItem]) => {
						const itemId = parseInt(itemIdStr, 10);

						return {
							itemId,
							quantity: cartItem.quantity,
							price: parseFloat(String(cartItem.price)) || 0,
							schedule: context.effectiveDeaSchedules[itemId]?.schedule ?? null,
							sizeCount: context.itemSizeCounts?.[itemId],
						};
					}),
					maximumPercent:
						controlCheck.crmMaximumControlPercent ??
						controlCheck.maximumControlPercent,
					unitType:
						controlCheck.crmUnitType ?? controlCheck.unitType ?? 'dollarAmount',
				},
				controlCheck.enabled === true ? controlCheck.crm : 'off',
			);

			if (controlled != null) {
				record({
					type: 'controlledSubstancePercentage',
					level: toMessageLevel(controlled.level),
					limit: controlled.maximumPercent,
					current: controlled.actualPercentage,
					message: `Controlled substance percentage (${controlled.actualPercentage.toFixed(1)}%) exceeds maximum (${controlled.maximumPercent}%)`,
					data: {
						actualPercentage: controlled.actualPercentage,
						maxPercentage: controlled.maximumPercent,
						restrictedValue: controlled.restrictedValue,
						totalValue: controlled.totalValue,
						unitType: controlled.unitType,
					},
				});
			}
		}

		this.orderValidationState = {
			hasError,
			hasWarning,
			messages,
			timestamp: Date.now(),
		};

		this.notifyOrderListeners();
	}

	/**
	 * Subscribe to order-level validation state changes
	 */
	subscribeToOrder(listener: OrderValidationListener): () => void {
		this.orderListeners.add(listener);

		// Immediately call listener with current state
		if (this.orderValidationState != null) {
			listener(this.orderValidationState);
		}

		return () => {
			this.orderListeners.delete(listener);
		};
	}

	/**
	 * Get current order-level validation state
	 */
	getOrderValidationState(): OrderValidationState | null {
		return this.orderValidationState;
	}

	/**
	 * Notify all listeners for an item
	 */
	private notifyListeners(
		itemId: number,
		validationState: ValidationState,
	): void {
		const itemListeners = this.listeners.get(itemId);

		if (itemListeners != null) {
			itemListeners.forEach((listener) => {
				try {
					listener(itemId, validationState);
				} catch (error) {
					// Error: CartValidationManager Error in validation listener
				}
			});
		}
	}

	/**
	 * Notify all order-level listeners
	 */
	private notifyOrderListeners(): void {
		if (this.orderValidationState == null) {
			return;
		}

		this.orderListeners.forEach((listener) => {
			try {
				listener(this.orderValidationState!);
			} catch (error) {
				// Error: CartValidationManager Error in order validation listener
			}
		});
	}

	/**
	 * Clean up resources
	 */
	public cleanup(): void {
		this.listeners.clear();
		this.orderListeners.clear();
		this.validationStates.clear();
		this.orderValidationState = null;
		this.cartItems = {};
		this.context = null;
		this.orderTotals = { subtotal: 0, discount: 0, total: 0 };
	}
}

// Export singleton instance
export const CartValidationManager = new CartValidationManagerClass();
export type {
	AddressControls,
	AddressLicense,
	ContractPriceBreak,
	ControlledSubstanceCheck,
	OrderValidationState,
	ValidationContext,
	ValidationMessage,
	ValidationState,
};
